<template>
  <div class="users-admin-page">
    <div class="toolbar">
      <div class="title-block">
        <h2>Пользователи</h2>
        <span class="counter">{{ filteredUsers.length }} / {{ users.length }}</span>
      </div>

      <div class="controls">
        <button class="btn" type="button" :disabled="isLoading" @click="loadUsers">
          {{ isLoading ? 'Обновляем...' : 'Обновить' }}
        </button>
        <button class="btn primary" type="button" @click="openCreate">Создать</button>
      </div>
    </div>

    <div class="filters">
      <label class="field search-field">
        <span>Поиск</span>
        <input v-model.trim="filters.query" class="input" type="text" placeholder="ФИО, email, телефон, TG" />
      </label>

      <label class="field">
        <span>Роль</span>
        <select v-model="filters.roleId" class="select">
          <option value="">Все роли</option>
          <option v-for="role in roles" :key="role.id" :value="String(role.id)">
            {{ role.name }}
          </option>
        </select>
      </label>

      <label class="field">
        <span>Статус</span>
        <select v-model="filters.status" class="select">
          <option value="active">Активные</option>
          <option value="inactive">Отключенные</option>
          <option value="all">Все</option>
        </select>
      </label>

      <button class="btn filter-reset" type="button" @click="resetFilters">Сбросить</button>
    </div>

    <div v-if="lastPassword.password" class="password-result">
      <div>
        <strong>{{ lastPassword.email }}</strong>
        <span>Новый пароль: {{ lastPassword.password }}</span>
      </div>
      <button class="mini-btn" type="button" @click="copyPassword(lastPassword.password)">Копировать</button>
    </div>

    <my-loader v-if="isLoading && users.length === 0" />

    <div v-else class="table-wrap">
      <table v-if="filteredUsers.length" class="users-table">
        <colgroup>
          <col class="col-status" />
          <col class="col-name" />
          <col class="col-email" />
          <col class="col-role" />
          <col class="col-phone" />
          <col class="col-tg" />
          <col class="col-ati" />
          <col class="col-signature" />
          <col class="col-actions" />
        </colgroup>
        <thead>
          <tr>
            <th>Статус</th>
            <th>ФИО</th>
            <th>Email</th>
            <th>Роль</th>
            <th>Телефон</th>
            <th>Telegram</th>
            <th>ATI</th>
            <th>Подпись</th>
            <th>Действия</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="user in filteredUsers" :key="user.id">
            <td>
              <span :class="['status-badge', user.is_active ? 'status-active' : 'status-inactive']">
                {{ user.is_active ? 'Активен' : 'Отключен' }}
              </span>
            </td>
            <td>
              <button class="text-button name-button" type="button" @click="openEdit(user)">
                {{ user.name || '—' }}
              </button>
            </td>
            <td :title="user.email">{{ user.email || '—' }}</td>
            <td>
              <span class="role-pill">{{ user.role?.name || '—' }}</span>
            </td>
            <td>{{ user.phone || '—' }}</td>
            <td>
              <span v-if="user.tg_username" class="tg-pill">{{ formatTg(user.tg_username) }}</span>
              <span v-else class="muted">—</span>
            </td>
            <td>
              <span :class="['soft-pill', user.ati_id || user.ati_token ? 'soft-pill-blue' : 'soft-pill-gray']">
                {{ atiText(user) }}
              </span>
            </td>
            <td>
              <span :class="['soft-pill', signatureCount(user) ? 'soft-pill-green' : 'soft-pill-gray']">
                {{ signatureCount(user) ? signatureCount(user) : 'Нет' }}
              </span>
            </td>
            <td>
              <div class="actions">
                <button class="act-btn" type="button" title="Редактировать" @click="openEdit(user)">Ред.</button>
                <button class="act-btn" type="button" title="Сбросить пароль"
                  @click="resetPassword(user)">Пароль</button>
                <button class="act-btn" :class="user.is_active ? 'danger' : 'success'" type="button"
                  :title="user.is_active ? 'Отключить' : 'Включить'" @click="toggleActive(user)">
                  {{ user.is_active ? 'Откл.' : 'Вкл.' }}
                </button>
                <button v-if="canImpersonate && user.is_active" class="act-btn" type="button"
                  title="Открыть сайт под этим пользователем" @click="impersonate(user)">
                  Вход
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-else class="state-block">
        Пользователей по выбранным фильтрам нет.
      </div>
    </div>

    <div v-if="panelOpen" class="modal-overlay" @click.self="closePanel">
      <div class="modal">
        <div class="drawer-header">
          <div>
            <h2>{{ drawerMode === 'create' ? 'Новый пользователь' : 'Редактирование' }}</h2>
            <span v-if="drawerMode === 'edit'" class="drawer-subtitle">{{ form.email }}</span>
          </div>
          <button class="close-btn" type="button" @click="closePanel">×</button>
        </div>

        <div v-if="createdPassword" class="created-password">
          <span>Пароль: {{ createdPassword }}</span>
          <button class="mini-btn" type="button" @click="copyPassword(createdPassword)">Копировать</button>
        </div>

        <form class="drawer-form" @submit.prevent="saveUser">
          <label class="field wide">
            <span>ФИО</span>
            <input v-model.trim="form.name" class="input" type="text" required />
          </label>

          <label class="field wide">
            <span>Email</span>
            <input v-model.trim="form.email" class="input" type="email" required />
          </label>

          <label class="field">
            <span>Телефон</span>
            <input v-model.trim="form.phone" class="input" type="text" placeholder="+7XXXXXXXXXX" />
          </label>

          <label class="field">
            <span>ATI ID</span>
            <input v-model.number="form.ati_id" class="input" type="number" />
          </label>

          <label class="field">
            <span>ATI token</span>
            <input v-model.trim="form.ati_token" class="input" type="text" />
          </label>

          <label class="field">
            <span>Telegram username</span>
            <input v-model.trim="form.tg_username" class="input" type="text" placeholder="@username" />
          </label>

          <label class="field">
            <span>Telegram chat id</span>
            <input v-model.number="form.tg_chat_id" class="input" type="number" />
          </label>

          <label v-if="drawerMode === 'create'" class="field wide">
            <span>Пароль</span>
            <input v-model.trim="form.password" class="input" type="text"
              placeholder="Автоматически, если оставить пустым" />
          </label>

          <label class="field">
            <span>Роль</span>
            <select v-model="form.role_id" class="select" required>
              <option disabled value="">Выберите роль</option>
              <option v-for="role in roles" :key="role.id" :value="String(role.id)">
                {{ role.name }}
              </option>
            </select>
          </label>

          <label class="checkbox-field wide">
            <input v-model="form.is_active" type="checkbox" />
            <span>Активная учетная запись</span>
          </label>

          <UserSignatureManager
            v-if="form.id"
            class="wide"
            :user-id="String(form.id)"
            @changed="loadUsers"
          />

          <div class="drawer-actions wide">
            <button class="btn primary" type="submit" :disabled="saving">
              {{ saving ? 'Сохраняем...' : 'Сохранить' }}
            </button>
            <button class="btn" type="button" @click="closePanel">Отмена</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, reactive, ref } from 'vue';
import { useStore } from 'vuex';
import { createToaster } from '@meforma/vue-toaster';
import UserSignatureManager from '@/pages/users/user-routing/UserSignatureManager.vue';

function blankForm() {
  return {
    id: null,
    name: '',
    email: '',
    phone: '',
    role_id: '',
    role_ids: [],
    is_active: true,
    ati_id: null,
    ati_token: '',
    tg_username: '',
    tg_chat_id: null,
    password: '',
  };
}

function normalizeOptional(value) {
  if (value === '' || value === undefined) return null;
  return value;
}

export default {
  name: 'UsersList',
  components: { UserSignatureManager },
  setup() {
    const store = useStore();
    const toaster = createToaster();
    const filters = reactive({ query: '', roleId: '', status: 'active' });
    const panelOpen = ref(false);
    const drawerMode = ref('create');
    const saving = ref(false);
    const createdPassword = ref('');
    const lastPassword = reactive({ email: '', password: '' });
    const form = reactive(blankForm());

    const users = computed(() => store.state.user.users || []);
    const roles = computed(() => store.state.user.roles_title || []);
    const isLoading = computed(() => store.state.user.statusLoading);
    const canImpersonate = computed(() => store.getters.hasPermission('users.impersonate'));

    const filteredUsers = computed(() => {
      const query = filters.query.toLowerCase();
      return users.value.filter((user) => {
        if (filters.status === 'active' && !user.is_active) return false;
        if (filters.status === 'inactive' && user.is_active) return false;
        if (filters.roleId && String(user.role?.id) !== filters.roleId) return false;
        if (!query) return true;
        return [
          user.name,
          user.email,
          user.phone,
          user.tg_username,
          user.tg_chat_id !== null && user.tg_chat_id !== undefined ? String(user.tg_chat_id) : null,
          user.role?.name,
        ].filter(Boolean).some((value) => String(value).toLowerCase().includes(query));
      });
    });

    const loadUsers = () => store.dispatch('getUsers');

    const resetFilters = () => {
      filters.query = '';
      filters.roleId = '';
      filters.status = 'active';
    };

    /*
    const rolesOptions = computed(() => {
      const result = roles.value.map((role) => ({
        id: Number(role.id),
        label: role.name
      }));
      return result;
    })
    */

    const fillForm = (source = blankForm()) => {
      Object.assign(form, blankForm(), {
        id: source.id || null,
        name: source.name || '',
        email: source.email || '',
        phone: source.phone || '',
        role_id: source.role?.id ? String(source.role.id) : '',
        is_active: source.is_active !== false,
        ati_id: source.ati_id || null,
        ati_token: source.ati_token || '',
        tg_username: source.tg_username || '',
        tg_chat_id: source.tg_chat_id || null,
        password: '',
      });
    };

    const openCreate = () => {
      drawerMode.value = 'create';
      createdPassword.value = '';
      fillForm();
      panelOpen.value = true;
    };

    const openEdit = (user) => {
      drawerMode.value = 'edit';
      createdPassword.value = '';
      fillForm(user);
      panelOpen.value = true;
    };

    const closePanel = () => {
      panelOpen.value = false;
      createdPassword.value = '';
    };

    const payloadFromForm = () => ({
      name: form.name,
      email: form.email,
      phone: normalizeOptional(form.phone),
      role_id: form.role_id ? Number(form.role_id) : null,
      is_active: Boolean(form.is_active),
      ati_id: normalizeOptional(form.ati_id),
      ati_token: normalizeOptional(form.ati_token),
      tg_username: normalizeOptional(form.tg_username),
      tg_chat_id: normalizeOptional(form.tg_chat_id),
    });

    const saveUser = async () => {
      if (saving.value) return;
      saving.value = true;
      try {
        if (drawerMode.value === 'create') {
          const payload = payloadFromForm();
          if (form.password) payload.password = form.password;
          const result = await store.dispatch('createUserAdmin', payload);
          if (result?.user) {
            createdPassword.value = result.password || '';
            lastPassword.email = result.user.email;
            lastPassword.password = result.password || '';
            toaster.success('Пользователь создан');
            drawerMode.value = 'edit';
            fillForm(result.user);
            if (!result.password) {
              createdPassword.value = '';
            }
          }
        } else {
          const result = await store.dispatch('updateUserAdmin', {
            userId: form.id,
            form: payloadFromForm(),
          });
          if (result?.id) {
            toaster.success('Пользователь обновлен');
            closePanel();
          }
        }
      } finally {
        saving.value = false;
      }
    };

    const toggleActive = async (user) => {
      const result = await store.dispatch('setUserActiveAdmin', {
        userId: user.id,
        isActive: !user.is_active,
      });
      if (result?.id) {
        toaster.success(user.is_active ? 'Пользователь отключен' : 'Пользователь включен');
      }
    };

    const resetPassword = async (user) => {
      if (!window.confirm(`Сбросить пароль для ${user.email}?`)) return;
      const result = await store.dispatch('resetUserPasswordAdmin', user.id);
      if (result?.password) {
        lastPassword.email = user.email;
        lastPassword.password = result.password;
        toaster.success('Пароль обновлен');
      }
    };

    const impersonate = async (user) => {
      // Окно открываем СИНХРОННО в жесте клика (до await), иначе блокировщик всплывающих окон.
      const popup = window.open(`${location.origin}/?imp=1`, '_blank');
      if (!popup) {
        toaster.error('Разрешите всплывающие окна для этого сайта');
        return;
      }
      try {
        const data = await store.dispatch('impersonateUser', user.id);
        if (!data || !data.access_token) {
          popup.close();
          toaster.error('Не удалось получить токен');
          return;
        }
        const token = data.access_token;
        const name = user.name || user.email || 'пользователь';
        const origin = location.origin;
        let handedOff = false;
        const onReady = (event) => {
          if (event.origin !== origin) return;
          if (event.source !== popup) return;
          if (event.data && event.data.type === 'IMP_READY') {
            window.removeEventListener('message', onReady);
            popup.postMessage({ type: 'IMP_TOKEN', token, name }, origin);
            handedOff = true;
          }
        };
        window.addEventListener('message', onReady);
        // Фолбэк: если READY не пришёл за 6с — закрываем окно.
        setTimeout(() => {
          if (!handedOff) {
            window.removeEventListener('message', onReady);
            try { popup.close() } catch { /* ignore */ }
            toaster.error('Окно не ответило. Разрешите всплывающие окна.');
          }
        }, 6000);
      } catch (error) {
        try { popup.close() } catch { /* ignore */ }
        toaster.error('Ошибка входа под пользователем');
      }
    };

    const copyPassword = async (password) => {
      if (!password) return;
      await navigator.clipboard?.writeText(password);
      toaster.success('Пароль скопирован');
    };

    const formatTg = (value) => {
      if (!value) return '—';
      return String(value).startsWith('@') ? value : `@${value}`;
    };

    const atiText = (user) => {
      if (user.ati_id && user.ati_token) return `ID ${user.ati_id}, token`;
      if (user.ati_id) return `ID ${user.ati_id}`;
      if (user.ati_token) return 'token';
      return 'Нет';
    };

    const signatureCount = (user) => {
      const fromTable = Number(user?.signatures_count);
      if (Number.isFinite(fromTable) && fromTable > 0) return fromTable;
      return user?.signature ? 1 : 0;
    };

    onMounted(() => {
      loadUsers();
    });

    return {
      filters,
      form,
      users,
      roles,
      isLoading,
      filteredUsers,
      panelOpen,
      drawerMode,
      saving,
      createdPassword,
      lastPassword,
      //rolesOptions,
      loadUsers,
      resetFilters,
      openCreate,
      openEdit,
      closePanel,
      saveUser,
      toggleActive,
      resetPassword,
      copyPassword,
      formatTg,
      atiText,
      canImpersonate,
      impersonate,
      signatureCount,
    };
  },
};
</script>

<style scoped>
.users-admin-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 14px 16px;
  color: #333;
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 10px;
}

.title-block {
  display: flex;
  align-items: baseline;
  gap: 10px;
}

h1,
h2 {
  margin: 0;
}

h1 {
  font-size: 22px;
  line-height: 1.2;
}

h2 {
  font-size: 18px;
}

.counter,
.drawer-subtitle,
.muted {
  color: #667085;
  font-size: 13px;
}

.controls,
.actions,
.drawer-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.filters {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: flex-end;
  margin-bottom: 12px;
  padding: 10px;
  background: #f7f8fa;
  border: 1px solid #edf0f3;
  border-radius: 6px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 3px;
  color: #4b5563;
  font-size: 12px;
}

.field.wide {
  grid-column: 1 / -1;
}

.search-field {
  min-width: 280px;
}

.input,
.select {
  height: 32px;
  border: 1px solid #d7dce2;
  border-radius: 4px;
  background: #fff;
  color: #333;
  font-size: 13px;
  padding: 0 8px;
}

.input {
  min-width: 150px;
}

.select {
  min-width: 150px;
}

.btn,
.mini-btn,
.act-btn,
.close-btn,
.text-button {
  border: 1px solid #d7dce2;
  background: #fff;
  color: #333;
  cursor: pointer;
  font-size: 13px;
}

.btn {
  height: 32px;
  border-radius: 4px;
  padding: 0 12px;
}

.btn:hover,
.act-btn:hover,
.mini-btn:hover {
  border-color: #007bff;
  color: #007bff;
}

.btn.primary {
  background: #007bff;
  border-color: #007bff;
  color: #fff;
}

.btn:disabled {
  cursor: default;
  opacity: 0.6;
}

.filter-reset {
  margin-left: auto;
}

.password-result,
.created-password {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
  padding: 8px 10px;
  border: 1px solid #bfdbfe;
  border-radius: 6px;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 13px;
}

.password-result div {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.mini-btn {
  height: 26px;
  border-radius: 4px;
  padding: 0 8px;
  flex-shrink: 0;
}

.table-wrap {
  flex: 1;
  overflow: auto;
}

.users-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 13px;
}

.users-table th,
.users-table td {
  height: 32px;
  padding: 6px 8px;
  border-bottom: 1px solid #edf0f3;
  text-align: left;
  vertical-align: middle;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.users-table th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #f7f8fa;
  color: #444;
  font-weight: 700;
}

.users-table tbody tr:hover {
  background: #f3f6fb;
}

.col-status {
  width: 100px;
}

.col-name {
  width: 190px;
}

.col-email {
  width: 230px;
}

.col-role {
  width: 150px;
}

.col-phone {
  width: 135px;
}

.col-tg {
  width: 150px;
}

.col-ati {
  width: 130px;
}

.col-signature {
  width: 90px;
}

.col-actions {
  width: 230px;
}

.status-badge,
.role-pill,
.tg-pill,
.soft-pill {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  border-radius: 10px;
  padding: 2px 7px;
  font-size: 12px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
}

.status-active {
  background: #e6f4ea;
  color: #1e7e34;
}

.status-inactive {
  background: #fdeaea;
  color: #c0392b;
}

.role-pill {
  background: #e8eefc;
  color: #2563eb;
}

.tg-pill {
  background: #eef4ff;
  color: #0b63ce;
}

.soft-pill-blue {
  background: #e8eefc;
  color: #2563eb;
}

.soft-pill-green {
  background: #e6f4ea;
  color: #1e7e34;
}

.soft-pill-gray {
  background: #edf0f3;
  color: #667085;
}

.text-button {
  padding: 0;
  border: 0;
  background: transparent;
  text-align: left;
}

.name-button {
  color: #0b63ce;
  font-weight: 600;
}

.name-button:hover {
  text-decoration: underline;
}

.act-btn {
  height: 26px;
  border-radius: 4px;
  padding: 0 7px;
  font-size: 12px;
}

.act-btn.danger {
  border-color: #e6a2a2;
  color: #9f2f2f;
}

.act-btn.success {
  border-color: #8fcaa6;
  color: #0b6b44;
}

.state-block {
  padding: 32px;
  color: #667085;
  font-size: 14px;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.45);
}

.modal {
  width: min(640px, calc(100vw - 24px));
  max-height: 90vh;
  overflow-y: auto;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.25);
  padding: 22px;
  max-width: 90vw;
  line-height: 24px;
}

.drawer-header {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 16px;
}

.close-btn {
  width: 30px;
  height: 30px;
  border-radius: 4px;
  font-size: 20px;
  line-height: 1;
}

.drawer-form {
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 16px 24px;
  align-items: flex-start;
}

.drawer-form .input,
.drawer-form .select {
  width: 100%;
  min-width: 0;
}

.drawer-form .field,
.drawer-form .checkbox-field,
.drawer-form .wide {
  grid-column: 1 / -1;
}

.checkbox-field {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #333;
  font-size: 13px;
}

@media (max-width: 900px) {
  .toolbar {
    align-items: flex-start;
    flex-direction: column;
  }

  .filter-reset {
    margin-left: 0;
  }

  .drawer-form {
    grid-template-columns: 1fr;
  }
}
</style>
